import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import SearchableSelect from 'components/SearchableSelect'
import { surface, line, radius, font, transition } from 'features/dashboard/tokens'
import { scoreColour } from 'features/dashboard/charts/scoreColour'
import { vettingMeta } from 'features/contractors/contractorMeta'

const PAGE = 8

const SORTS = [
  ['overdue', 'Most overdue'],
  ['assigned', 'Most assigned'],
  ['completion', 'Completion %'],
  ['ontime', 'On-time %'],
]

const ContractorCard = ({ c, onOpen }) => {
  const assigned = c.assigned || 0
  const completed = c.completed || 0
  const onTime = Math.max(0, completed - (c.completedLate || 0))
  const late = c.completedLate || 0
  const overdue = c.overdue || 0
  const pending = Math.max(0, assigned - onTime - late - overdue)
  const rate = assigned > 0 ? Math.round((completed / assigned) * 100) : 0
  const tint = scoreColour(rate)
  const hasOnTime = c.onTimeRate !== null && c.onTimeRate !== undefined
  const seg = n => (assigned > 0 ? `${(n / assigned) * 100}%` : '0%')
  const m = c.vettingStatus ? vettingMeta(c.vettingStatus) : null
  const canFocus = !!onOpen

  return (
    <Row
      $clickable={canFocus}
      onClick={canFocus ? () => onOpen(c.contractorId) : undefined}
      title={canFocus ? `Open ${c.name} in the contractors area` : undefined}
    >
      <Main>
        <Name>
          {c.name}
          {c.company && c.company !== c.name && <Company> · {c.company}</Company>}
          {m && <Vet $fg={m.tint} $bg={m.bg} title={`Vetting status: ${m.label}`}>{m.label}</Vet>}
        </Name>
        <Meta>
          <Chip>{assigned} assigned</Chip>
          <Chip $tone='ok'>{completed} done</Chip>
          {overdue > 0 && <Chip $tone='risk'>{overdue} overdue</Chip>}
          {late > 0 && <Chip $tone='warn'>{late} late</Chip>}
          {hasOnTime && (
            <Chip $tone='ontime' title='On-time rate: of the tasks this contractor completed, the share finished on or before the deadline.'>
              {Math.round(c.onTimeRate)}% on time
            </Chip>
          )}
        </Meta>
        {c.trade && <Domains title='What this contractor is mostly contracted for.'>Mostly: {c.trade}</Domains>}
        <Bar title={`${assigned} assigned · ${onTime} on time · ${late} late · ${overdue} overdue · ${pending} not started`}>
          {onTime > 0 && <Seg style={{ width: seg(onTime), background: '#15803d' }} />}
          {late > 0 && <Seg style={{ width: seg(late), background: '#d97706' }} />}
          {overdue > 0 && <Seg style={{ width: seg(overdue), background: '#b00020' }} />}
          {pending > 0 && <Seg style={{ width: seg(pending), background: '#cbd5e1' }} />}
        </Bar>
        <Legend>
          <LItem><Dot $tint='#15803d' /><LLabel>On time</LLabel></LItem>
          <LItem><Dot $tint='#d97706' /><LLabel>Late</LLabel></LItem>
          {overdue > 0 && <LItem><Dot $tint='#b00020' /><LLabel>Overdue</LLabel></LItem>}
          <LItem><Dot $tint='#cbd5e1' /><LLabel>Not started</LLabel></LItem>
        </Legend>
      </Main>
      {canFocus && (
        <FocusBtn onClick={e => { e.stopPropagation(); onOpen(c.contractorId) }} title={`Open ${c.name} in the contractors area`}>
          View tasks →
        </FocusBtn>
      )}
      <Rate $tint={tint} title='Completion rate: tasks completed ÷ tasks assigned.'>{`${rate}%`}</Rate>
    </Row>
  )
}

const ContractorPerformanceWidget = ({ data, scoped = false, onOpen }) => {
  const rows = data?.rows || []
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState('overdue')
  const [visible, setVisible] = useState(PAGE)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = q
      ? rows.filter(r => `${r.name || ''} ${r.company || ''} ${r.trade || ''}`.toLowerCase().includes(q))
      : rows
    const compRate = r => (r.assigned ? (r.completed || 0) / r.assigned : 0)
    const cmp = {
      overdue: (a, b) => (b.overdue || 0) - (a.overdue || 0),
      assigned: (a, b) => (b.assigned || 0) - (a.assigned || 0),
      completion: (a, b) => compRate(b) - compRate(a),
      ontime: (a, b) => (b.onTimeRate ?? -1) - (a.onTimeRate ?? -1),
    }[sort]
    return [...list].sort(cmp)
  }, [rows, query, sort])

  const shown = filtered.slice(0, visible)

  if (rows.length === 0) {
    return (
      <Empty>
        <strong>No contractors have tasks {scoped ? 'in this view' : 'yet'}.</strong>
        <br />
        Assign a task to a contractor (from the task’s page) and you’ll see how they’re delivering (assigned, completed, on time and overdue) here.
        {onOpen && <FootBtn type='button' onClick={() => onOpen()}>Open contractors →</FootBtn>}
      </Empty>
    )
  }

  return (
    <Wrap>
      <Head>
        <Count>
          Showing {shown.length} of {filtered.length}
          {filtered.length !== rows.length ? ` (${rows.length} total)` : ''}
        </Count>
      </Head>
      <Controls>
        <Search
          value={query}
          onChange={e => { setQuery(e.target.value); setVisible(PAGE) }}
          placeholder='Search contractors…'
          aria-label='Search contractors by name'
        />
        <SortLabel>
          Sort
          <SearchableSelect
            value={sort}
            onChange={v => { setSort(v); setVisible(PAGE) }}
            options={SORTS.map(([k, l]) => ({ value: k, label: l }))}
            ariaLabel='Sort contractors'
          />
        </SortLabel>
      </Controls>
      {filtered.length === 0 ? (
        <Empty>No contractors match “{query}”.</Empty>
      ) : (
        <Scroll>
          <List>
            {shown.map(c => <ContractorCard key={c.contractorId} c={c} onOpen={onOpen} />)}
          </List>
          <InfiniteScrollSentinel
            hasMore={shown.length < filtered.length}
            loading={false}
            onLoadMore={() => setVisible(v => v + PAGE)}
          />
        </Scroll>
      )}
      {onOpen && <FootBtn type='button' onClick={() => onOpen()}>All contractors →</FootBtn>}
    </Wrap>
  )
}

export default ContractorPerformanceWidget

const Wrap = styled.div`
  display: flex; flex-direction: column; min-height: 0; height: 100%;
`
const Head = styled.div`
  display: flex; align-items: center; justify-content: flex-end; margin-bottom: 8px;
`
const Count = styled.span`
  font-size: ${font.caption}; font-weight: 700; color: ${({ theme }) => theme.colors.grey[800]};
`
const Controls = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: 10px; flex-wrap: wrap;
`
const Search = styled.input`
  flex: 1; min-width: 140px;
  padding: 7px 11px; border-radius: ${radius.sm}; font: inherit; font-size: 13px;
  border: 1px solid ${line.base}; background: ${surface.card}; color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: none; border-color: ${line.strong}; }
`
const SortLabel = styled.label`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: ${font.caption}; font-weight: 700; color: ${({ theme }) => theme.colors.grey[800]};
`
const Scroll = styled.div`
  flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden;
  scrollbar-width: thin;
`
const List = styled.div` display: flex; flex-direction: column; gap: 8px; `

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 12px;
  padding: 10px;
  background: ${surface.wash};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  align-items: center;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: ${transition};
  &:hover { ${({ $clickable }) => ($clickable ? 'border-color: #33becc; background: #fff;' : '')} }
`
const Main = styled.div` display: flex; flex-direction: column; gap: 4px; min-width: 0; `
const Name = styled.span`
  font-size: ${font.body}; font-weight: 700; color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden; text-overflow: ellipsis;
`
const Company = styled.span` font-weight: 500; color: ${({ theme }) => theme.colors.grey[800]}; `
const Vet = styled.span`
  display: inline-block; margin-left: 8px; padding: 1px 8px; border-radius: ${radius.pill};
  font-size: 10px; font-weight: 800; vertical-align: middle;
  color: ${({ $fg }) => $fg}; background: ${({ $bg }) => $bg};
`
const Meta = styled.div` display: flex; flex-wrap: wrap; gap: 4px; `
const Domains = styled.div`
  font-size: ${font.caption}; color: ${({ theme }) => theme.colors.grey[800]}; margin-top: 3px;
`
const Chip = styled.span`
  font-size: ${font.caption}; font-weight: 700; letter-spacing: 0.04em;
  padding: 2px 8px; border-radius: ${radius.pill};
  ${({ $tone, theme }) => $tone === 'ok'
    ? 'background: #2aa6b315; color: #2aa6b3; border: 1px solid #2aa6b335;'
    : $tone === 'risk'
    ? 'background: #b0002015; color: #b00020; border: 1px solid #b0002035;'
    : $tone === 'warn'
    ? 'background: #d9770615; color: #d97706; border: 1px solid #d9770635;'
    : $tone === 'ontime'
    ? 'background: #15803d15; color: #15803d; border: 1px solid #15803d35;'
    : `background: ${surface.wash}; color: ${theme.colors.grey[800]}; border: 1px solid ${line.base};`}
`
const Bar = styled.div`
  display: flex; height: 8px; margin-top: 6px;
  background: ${surface.wash}; border: 1px solid ${line.base}; border-radius: ${radius.pill}; overflow: hidden;
`
const Seg = styled.div` height: 100%; transition: ${transition}; `
const Legend = styled.div` display: flex; flex-wrap: wrap; gap: 4px 12px; margin-top: 6px; `
const LItem = styled.span` display: inline-flex; align-items: center; gap: 5px; `
const Dot = styled.span` width: 8px; height: 8px; border-radius: 50%; background: ${({ $tint }) => $tint}; `
const LLabel = styled.span` font-size: ${font.caption}; color: ${({ theme }) => theme.colors.grey[800]}; `
const FocusBtn = styled.button`
  font: inherit; padding: 5px 13px; background: transparent; color: #1f8c97;
  border: 1px solid rgba(51, 190, 204, 0.45); border-radius: 999px;
  font-size: 11px; font-weight: 800; letter-spacing: 0.04em; cursor: pointer; white-space: nowrap;
  transition: all 160ms ease;
  &:hover { background: rgba(51,190,204,0.10); }
`
const Rate = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title}; letter-spacing: -0.4px; color: ${({ $tint }) => $tint};
  font-variant-numeric: tabular-nums; font-weight: 500;
`
const Empty = styled.div`
  padding: 20px; text-align: center; border: 1px dashed ${line.base}; border-radius: ${radius.md};
  font-size: ${font.body}; line-height: 1.55; color: ${({ theme }) => theme.colors.grey[800]};
`
const FootBtn = styled.button`
  align-self: center; margin-top: 10px;
  font: inherit; font-size: 12.5px; font-weight: 700; cursor: pointer;
  background: transparent; border: none; color: #1f8c97; padding: 2px 0;
  &:hover { text-decoration: underline; }
`
